import { useState, useEffect, useRef, useCallback } from "react";

interface UseTypewriterEffectOptions {
  /** Speed of typewriter effect in milliseconds per character */
  speed?: number;
  /** Whether to start typewriter effect immediately */
  startImmediately?: boolean;
  /** Callback when typewriter finishes displaying current content */
  onComplete?: () => void;
  /**
   * Spans matching this pattern are revealed in one step instead of character
   * by character. Used for citation markers, which would otherwise flash a
   * half-written "[[cite:S" before resolving into a chip.
   *
   * The pattern must be global; it is cloned before use so the caller's
   * lastIndex is never mutated.
   */
  atomicPattern?: RegExp;
}

export function useTypewriterEffect(
  targetText: string,
  options: UseTypewriterEffectOptions = {}
) {
  const {
    speed = 20, // Very fast for real-time feel
    startImmediately = true,
    onComplete,
    atomicPattern,
  } = options;

  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentIndexRef = useRef(0);
  const targetTextRef = useRef(targetText);

  // Update target text ref when targetText changes
  useEffect(() => {
    targetTextRef.current = targetText;
  }, [targetText]);

  const atomicPatternRef = useRef<RegExp | undefined>(atomicPattern);
  atomicPatternRef.current = atomicPattern;

  const startTypewriter = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    setIsTyping(true);

    intervalRef.current = setInterval(() => {
      const currentTarget = targetTextRef.current;
      const currentIndex = currentIndexRef.current;

      if (currentIndex >= currentTarget.length) {
        // Finished typing current content
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setIsTyping(false);
        onComplete?.();
        return;
      }

      // Add next character(s) - we can add multiple chars at once for speed
      let charsToAdd = Math.min(2, currentTarget.length - currentIndex); // Add 1-2 chars at a time

      // If an atomic span starts at or spans the cursor, jump to its end so it
      // is never displayed partially written.
      const pattern = atomicPatternRef.current;
      if (pattern) {
        const scan = new RegExp(pattern.source, pattern.flags.includes("g") ? pattern.flags : `${pattern.flags}g`);
        let m: RegExpExecArray | null;
        while ((m = scan.exec(currentTarget)) !== null) {
          const start = m.index;
          const end = start + m[0].length;
          if (end <= currentIndex) continue;
          if (start > currentIndex + charsToAdd) break;
          charsToAdd = end - currentIndex;
          break;
        }
      }

      const nextText = currentTarget.slice(0, currentIndex + charsToAdd);

      setDisplayedText(nextText);
      currentIndexRef.current += charsToAdd;
    }, speed);
  }, [speed, onComplete]);

  const stopTypewriter = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTyping(false);
    setDisplayedText(targetText);
    currentIndexRef.current = targetText.length;
  }, [targetText]);

  const reset = useCallback(() => {
    stopTypewriter();
    setDisplayedText("");
    currentIndexRef.current = 0;
  }, [stopTypewriter]);

  // Auto-start when content changes
  useEffect(() => {
    if (targetText.length > currentIndexRef.current && startImmediately) {
      startTypewriter();
    }
  }, [targetText, startTypewriter, startImmediately]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    displayedText,
    isTyping,
    startTypewriter,
    stopTypewriter,
    reset,
    progress:
      targetText.length > 0 ? currentIndexRef.current / targetText.length : 0,
  };
}


